import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileText, Plus, CheckCircle, XCircle, Clock, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { subscriptionService, SubscriptionAgreement } from '@/services/subscriptionService';
import { SubscriptionAgreementDialog } from './SubscriptionAgreementDialog';
import { useTenantStore } from '@/hooks/useTenantStore';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import * as CapLedgerService from '@/services/capLedgerService';

const STATUS_COLORS: Record<SubscriptionAgreement['status'], string> = {
  DRAFT: 'bg-slate-500',
  OPEN: 'bg-blue-500',
  SUBSCRIBED: 'bg-purple-500',
  PAID: 'bg-green-500',
  ISSUED: 'bg-emerald-600',
  CANCELLED: 'bg-red-500',
};

const STATUS_ICONS: Record<SubscriptionAgreement['status'], any> = {
  DRAFT: FileText,
  OPEN: Clock,
  SUBSCRIBED: CheckCircle,
  PAID: DollarSign,
  ISSUED: CheckCircle,
  CANCELLED: XCircle,
};

export function SubscriptionAgreementList() {
  const { tenantId } = useTenantStore();
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [shareholders, setShareholders] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);

  useEffect(() => {
    if (tenantId) {
      fetchData();
    }
  }, [tenantId]);

  const fetchData = async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      const [subsData, shareholdersData] = await Promise.all([
        subscriptionService.getSubscriptions(tenantId),
        supabase.from('shareholders').select('id, name').eq('company_id', tenantId),
      ]);

      setSubscriptions(subsData || []);
      setShareholders(shareholdersData.data || []);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id: string, newStatus: SubscriptionAgreement['status']) => {
    try {
      await subscriptionService.updateStatus(id, newStatus);
      toast.success('Status updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleRecordPayment = async (id: string) => {
    try {
      await subscriptionService.recordPayment(id, new Date().toISOString().split('T')[0]);
      toast.success('Payment recorded successfully');
      fetchData();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment');
    }
  };

  const handleIssueShares = async () => {
    if (!selectedSubscription || !tenantId) return;

    try {
      // Create ledger event
      const ledgerEventId = await CapLedgerService.insertLedgerEvent({
        companyId: tenantId,
        shareholderId: selectedSubscription.shareholder_id,
        eventType: 'ISSUANCE',
        shares: selectedSubscription.shares_offered,
        pricePerShare: selectedSubscription.price_per_share,
        shareClass: selectedSubscription.share_class,
        noteReference: `Subscription Agreement ${selectedSubscription.id}`,
      });

      // Update subscription with ledger event
      await subscriptionService.issueShares(selectedSubscription.id, ledgerEventId);

      toast.success('Shares issued successfully');
      setIssueDialogOpen(false);
      setSelectedSubscription(null);
      fetchData();
    } catch (error) {
      console.error('Error issuing shares:', error);
      toast.error('Failed to issue shares');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading subscriptions...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Subscription Agreements</CardTitle>
              <CardDescription>Manage share subscription agreements and issuances</CardDescription>
            </div>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Agreement
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No subscription agreements yet. Create your first one to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Subscriber</TableHead>
                  <TableHead>Shares</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Total Amount</TableHead>
                  <TableHead>Share Class</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscriptions.map((sub) => {
                  const StatusIcon = STATUS_ICONS[sub.status];
                  return (
                    <TableRow key={sub.id}>
                      <TableCell>
                        <Badge className={STATUS_COLORS[sub.status]}>
                          <StatusIcon className="mr-1 h-3 w-3" />
                          {sub.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{sub.shareholders?.name || 'N/A'}</TableCell>
                      <TableCell>{sub.shares_offered.toLocaleString()}</TableCell>
                      <TableCell>${sub.price_per_share.toFixed(4)}</TableCell>
                      <TableCell className="font-medium">
                        ${sub.total_amount.toLocaleString()}
                      </TableCell>
                      <TableCell>{sub.share_class}</TableCell>
                      <TableCell>{new Date(sub.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {sub.status === 'DRAFT' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(sub.id, 'OPEN')}
                            >
                              Open
                            </Button>
                          )}
                          {sub.status === 'OPEN' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(sub.id, 'SUBSCRIBED')}
                            >
                              Mark Subscribed
                            </Button>
                          )}
                          {sub.status === 'SUBSCRIBED' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRecordPayment(sub.id)}
                            >
                              Record Payment
                            </Button>
                          )}
                          {sub.status === 'PAID' && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedSubscription(sub);
                                setIssueDialogOpen(true);
                              }}
                            >
                              Issue Shares
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SubscriptionAgreementDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={fetchData}
        shareholders={shareholders}
      />

      <AlertDialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Issue Shares</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a ledger event and issue{' '}
              {selectedSubscription?.shares_offered.toLocaleString()} shares to{' '}
              {selectedSubscription?.shareholders?.name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleIssueShares}>Issue Shares</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
